/**
 * feel.ts — THE single table of tunable feel constants (AGENTS.md: feel constants live in one table).
 *
 * Every named constant in AGENTS.md lives here, plus per-clip attack windows and
 * turn caps. Nothing tunable may be hard-coded anywhere else in client, server or
 * shared code; import from here instead. The client's debug tuning panel edits
 * this table live, so keep it plain data (numbers only, no functions).
 */
/**
 * Attack clip identifiers. Baked trajectories are keyed by these.
 *
 * There are only two: every slash direction is the canonical `slash` trajectory
 * rolled about the character's forward axis by the dial angle (AGENTS.md
 * §Combat 1), so 360° of swings need one arc, not one clip per direction.
 */
export const CLIP_IDS = ['slash', 'stab'];
/**
 * Emotes: animations that are only ever a performance.
 *
 * Kept apart from CLIP_IDS rather than folded in, because an attack clip is
 * defined by its windup/active/recovery windows and its damage, and an emote has
 * none of those. A `Record<ClipId, ...>` that had to carry meaningless numbers
 * for two of its entries would be inviting someone to read them.
 *
 * They go through the same authoring and bake pipeline though, so the arms,
 * torso and hands-on-grip all solve for free.
 */
export const EMOTE_IDS = ['flourish', 'bow'];
/** Anything with a baked sword trajectory: attacks and emotes alike. */
export const POSED_IDS = [...CLIP_IDS, ...EMOTE_IDS];
export const isEmoteId = (id) => EMOTE_IDS.includes(id);
export const FEEL = {
    TICK_HZ: 60,
    SUBSAMPLE_HZ: 60,
    INTERP_DELAY_MS: 35,
    RECONCILE_MS: 90,
    RECONCILE_SNAP_M: 1.5,
    MOUSE_SENSITIVITY: 0.0022,
    CAMERA_DISTANCE: 4.2,
    CAMERA_HEIGHT: 1.9,
    EYE_HEIGHT: 1.68,
    FOV_FIRST_PERSON: 88,
    FOV_THIRD_PERSON: 70,
    PITCH_MIN: -0.9,
    PITCH_MAX: 0.7,
    FREE_LOOK_RETURN_MS: 140,
    MOVE_SPEED: 3.4,
    BACKPEDAL_MULT: 0.6,
    MOVE_MULT_ATTACKING: 0.55,
    MOVE_MULT_BLOCKING: 0.7,
    ACCEL: 15,
    FRICTION: 13,
    GAIT_CYCLE_M: 2.2,
    // 2.2 / 2π — the amplitude that holds the foot still at mid-stance.
    GAIT_STRIDE: 0.35,
    GAIT_LIFT: 0.13,
    TURN_CAP_ACTIVE: 2.6,
    TURN_SENS_ATTACKING: 0.39,
    TURN_CAP_IDLE: 20,
    V_REF: 9,
    DMG_MIN_MULT: 0.35,
    DMG_MAX_MULT: 1.8,
    HEAD_MULT: 1.75,
    LEG_MULT: 0.7,
    FOOT_MULT: 0.5,
    ARM_MULT: 0.65,
    ARM_RADIUS: 0.07,
    HEAD_ZONE_FRACTION: 0.18,
    LEG_ZONE_FRACTION: 0.35,
    FOOT_ZONE_FRACTION: 0.08,
    KNOCKBACK_SCALE: 0.35,
    ENCUMBRANCE_FREE_KG: 8,
    ENCUMBRANCE_REF_KG: 34,
    ENCUMBRANCE_MAX_SLOW: 0.12,
    PARRY_WINDOW_MS: 200,
    PARRY_STAGGER_MS: 700,
    // Brief stun after a landed hit — long enough to feel, short enough to rejoin.
    FLINCH_MS: 420,
    // Wide enough to click after the clang; short enough you cannot pocket it.
    RIPOSTE_WINDOW_MS: 700,
    // Enter near the wound-back park — faster than a rest swing, readable still.
    RIPOSTE_ENTRY_AT: 0.55,
    // Keep ~1/3 of the hit; the swing itself is not cancelled (hyper armour).
    RIPOSTE_DAMAGE_MULT: 0.34,
    BLOCK_CHIP_FRACTION: 0,
    BLOCK_HEIGHT: 1.35,
    BLOCK_PITCH_RANGE: 0.12,
    BLOCK_YAW: 0.62,
    BLOCK_ROLL: 0.45,
    BLOCK_REACH: 0.5,
    STAGGER_MOVE_MULT: 0.35,
    STAGGER_TURN_MULT: 0.25,
    // Long enough to hold a queued stab through a full stab recovery (no combo skip).
    BUFFER_MS: 900,
    COMBO_EARLY_MS: 160,
    CHAIN_BLEND_MS: 200,
    // Combos enter at the wound-back park; CHAIN_ENTRY_AT tracks WIND_BACK_AT so
    // a single knob moves both the hold pose and the combo start.
    CHAIN_ENTRY_AT: 0.62,
    CHAIN_REAIM_MIN: 0.35,
    // π/2 in ~130ms — finishes the roll inside a typical held combo windup.
    CHAIN_ANGLE_SLEW: 12,
    WIND_BACK_AT: 0.62,
    FEINT_BEFORE: 0.5,
    DIAL_PUSH_HALFLIFE_MS: 68,
    DIAL_MIN_PUSH_PX: 13,
    DIAL_SLEW_RATE: 21,
    DIAL_DEAD_ARC: Math.PI / 4,
    HITSTOP_BLOCK_MS: 60,
    HITSTOP_CHAMBER_MS: 140,
    HITSTOP_FLESH_MS: 90,
    CAMERA_SHAKE: 0.08,
    MAX_HEALTH: 100,
    RESPAWN_MS: 3000,
    ROUND_INTERMISSION_MS: 5000,
    BODY_HEIGHT: 1.8,
    BODY_RADIUS: 0.35,
    SWORD_RADIUS: 0.05,
    SWORD_LENGTH: 1.15,
    SLASH_PIVOT: [0, 1.5, 0],
    SWORD_GRIP_LENGTH: 0.24,
    SWING_STANDOFF: 0.4,
    SWING_RADIUS: 0.32,
    TORSO_TWIST_MAX: 0.62,
    TORSO_LEAN_MAX: 0.2,
    TORSO_LUNGE_MAX: 0.14,
    BOW_FOLD: 0.5,
    HIP_HEIGHT: 0.9,
    THIGH_BONE: 0.45,
    SHIN_BONE: 0.42,
    ANKLE_HEIGHT: 0.075,
    STANCE_HALF_WIDTH: 0.095,
    FOOT_LENGTH: 0.2,
    HEAD_RADIUS: 0.118,
    CHEST_RADIUS: 0.17,
    WAIST_RADIUS: 0.145,
    PELVIS_RADIUS: 0.165,
    THIGH_RADIUS: 0.092,
    SHIN_RADIUS: 0.072,
    FOOT_RADIUS: 0.06,
    CLIPS: {
        slash: { windupMs: 550, activeMs: 440, recoveryMs: 540, baseDamage: 33 },
        // Longer recovery is the anti-spam gap: stabs cannot combo-skip recovery.
        stab: { windupMs: 380, activeMs: 230, recoveryMs: 720, baseDamage: 20 },
    },
    EMOTES: {
        flourish: { durationMs: 1100 },
        bow: { durationMs: 1500 },
    },
};
/** Fixed timestep length in ms, derived from TICK_HZ. */
export const tickMs = () => 1000 / FEEL.TICK_HZ;
/** Total duration of one attack clip in ms. */
export const clipDurationMs = (clip) => {
    const t = FEEL.CLIPS[clip];
    return t.windupMs + t.activeMs + t.recoveryMs;
};
//# sourceMappingURL=feel.js.map